import { useState, useEffect, useRef, useMemo } from "react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Download, Search, Image, Mic, FileIcon, Loader2, Share2, RefreshCw } from "lucide-react";
import { ShareDialog } from "@/components/ShareDialog";
import { ReprocessChatButton } from "@/components/ReprocessChatButton";
import { PdfExportDialog, PdfExportOptions } from "@/components/PdfExportDialog";
import ChatMessage from "@/components/ChatMessage";
import FilterBar from "@/components/FilterBar";
import AiChatPanel from "@/components/AiChatPanel";
import Timeline from "@/components/Timeline";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { parseWhatsAppZip, WhatsAppMessage, ParsedChat } from "@/utils/whatsappParser";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { CostConfirmDialog } from "@/components/CostConfirmDialog";
import { CostTracker } from "@/components/CostTracker";
import { estimateRerunCaptionCost } from "@/utils/costEstimation";

// Mock data - will be replaced with real parsed data
const mockMessages: WhatsAppMessage[] = [
  {
    id: "1",
    sender: "John Doe",
    content: "Hey! How are you doing?",
    timestamp: new Date("2024-01-15T10:30:00"),
    isSent: false,
  },
  {
    id: "2",
    sender: "You",
    content: "I'm great! Just working on an exciting project.",
    timestamp: new Date("2024-01-15T10:31:00"),
    isSent: true,
  },
];

const Viewer = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [messages, setMessages] = useState<WhatsAppMessage[]>(mockMessages);
  const [stats, setStats] = useState({
    totalMessages: 0,
    imageCount: 0,
    audioCount: 0,
    documentCount: 0,
    videoCount: 0,
    contactCount: 0,
  });
  const [processing, setProcessing] = useState(true);
  const [progress, setProgress] = useState(0);
  const [progressStage, setProgressStage] = useState(t("loading.initializing"));
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiProgress, setAiProgress] = useState(0);
  const [aiStatusMessage, setAiStatusMessage] = useState('');
  const [rerunningAi, setRerunningAi] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [chatFileName, setChatFileName] = useState<string>("");
  const [chatLanguage, setChatLanguage] = useState<string>("en");
  const [attachmentAiData, setAttachmentAiData] = useState<Record<string, any>>({});
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const wsRef = useRef<Map<string, WebSocket>>(new Map());
  const [processingMessages, setProcessingMessages] = useState<Set<string>>(new Set());
  const prevProcessingRef = useRef(false);
  const [showRerunCostDialog, setShowRerunCostDialog] = useState(false);
  const [rerunCost, setRerunCost] = useState(0);
  const [pendingRerunMessageId, setPendingRerunMessageId] = useState<string | null>(null);
  const [rerunBudget, setRerunBudget] = useState<number | undefined>(undefined);
  const [totalCost, setTotalCost] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [costBreakdown, setCostBreakdown] = useState({
    upload: 0,
    chatQueries: 0,
    rerunCaptions: 0,
  });
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showPdfExportDialog, setShowPdfExportDialog] = useState(false);
  const [pdfProgress, setPdfProgress] = useState<{ active: boolean; step: string; percent: number }>({ active: false, step: '', percent: 0 });

  // Show toast when processing completes
  useEffect(() => {
    if (prevProcessingRef.current && !aiProcessing && processingMessages.size === 0) {
      toast({
        title: t("toast.processingComplete"),
        description: t("toast.allAttachmentsAnalyzed"),
      });
    }
    prevProcessingRef.current = aiProcessing;
  }, [aiProcessing, processingMessages.size, toast, t]);

  // Filter and search messages - must be before early returns
  const filteredMessages = useMemo(() => {
    let filtered = [...messages];

    // Apply attachment type filter by file extension
    if (activeFilter) {
      filtered = filtered.filter(msg => {
        if (!msg.attachment || !msg.attachment.fileName) return false;
        
        const ext = msg.attachment.fileName.split('.').pop()?.toLowerCase() || '';
        
        if (activeFilter === 'images') {
          return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
        } else if (activeFilter === 'audio') {
          return ['mp3', 'wav', 'm4a', 'ogg', 'opus', 'aac', 'flac'].includes(ext);
        } else if (activeFilter === 'documents') {
          return ['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'rtf', 'odt'].includes(ext);
        } else if (activeFilter === 'videos') {
          return ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v', 'mpeg', 'mpg'].includes(ext);
        } else if (activeFilter === 'contacts') {
          return ext === 'vcf';
        }
        return false;
      });
    }

    // Apply date range filter
    if (dateRange?.from) {
      const fromTime = dateRange.from.getTime();
      const toTime = dateRange.to ? dateRange.to.getTime() : fromTime + 86400000; // +1 day if no end date
      
      filtered = filtered.filter(msg => {
        const msgTime = msg.timestamp.getTime();
        return msgTime >= fromTime && msgTime <= toTime;
      });
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(msg => {
        // Search in message content
        if (msg.content.toLowerCase().includes(query)) return true;
        
        // Search in sender name
        if (msg.sender.toLowerCase().includes(query)) return true;
        
        // Search in AI descriptions
        if (msg.attachment?.aiDescription?.toLowerCase().includes(query)) return true;
        
        // Search in transcriptions
        if (msg.attachment?.transcription?.toLowerCase().includes(query)) return true;
        
        return false;
      });
    }

    return filtered;
  }, [messages, activeFilter, dateRange, searchQuery]);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) return;

      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .single();

      setIsAdmin(!!data);
    };

    checkAdminStatus();
  }, [user]);

  // Load and parse chat
  useEffect(() => {
    const loadChat = async () => {
      // Read chatId from URL query parameter first, then fallback to sessionStorage
      const urlParams = new URLSearchParams(window.location.search);
      const urlChatId = urlParams.get('id');
      const sessionChatId = sessionStorage.getItem('chatId');
      const storedChatId = urlChatId || sessionChatId;
      
      if (!storedChatId || !user) {
        setProcessing(false);
        return;
      }

      // Store in sessionStorage for future use
      if (urlChatId) {
        sessionStorage.setItem('chatId', urlChatId);
      }

      setChatId(storedChatId);

      try {
        // Get chat record
        const { data: chat, error: chatError } = await supabase
          .from('chat_analyses')
          .select('*')
          .eq('id', storedChatId)
          .maybeSingle();

        if (chatError) {
          console.error('Database error:', chatError);
          throw new Error('Failed to load chat from database');
        }

        if (!chat) {
          toast({
            title: t("toast.chatNotFound"),
            description: t("toast.chatMayBeDeleted"),
            variant: "destructive",
          });
          navigate("/history");
          return;
        }

        setChatFileName(chat.file_name);
        setChatLanguage(chat.language || 'en');

        // Load cost tracking data if exists
        if (chat.cost_tracking) {
          const costData = chat.cost_tracking as any;
          setTotalCost(costData.total || 0);
          setCostBreakdown(costData.breakdown || {
            upload: 0,
            chatQueries: 0,
            rerunCaptions: 0,
          });
        }

        // Load messages from database (already parsed during upload)
        if (chat.messages_data) {
          toast({
            title: t("toast.loadingChat"),
            description: t("toast.retrievingMessages"),
          });

          const parsedMessages = chat.messages_data as any[];
          
          // Load existing AI data from chat record (keyed by message ID)
          const existingAiData = typeof chat.attachment_ai_data === 'object' && chat.attachment_ai_data !== null 
            ? chat.attachment_ai_data as Record<string, any>
            : {};
          
          // Merge AI data with parsed messages
          const messagesWithAi = parsedMessages.map(msg => {
            if (msg.attachment && existingAiData[msg.id]) {
              const aiData = existingAiData[msg.id] as any;
              return {
                ...msg,
                attachment: {
                  ...msg.attachment,
                  aiDescription: aiData.aiDescription,
                  transcription: aiData.transcription
                }
              };
            }
            return msg;
          });
          
          setMessages(messagesWithAi);
          setAttachmentAiData(existingAiData as Record<string, any>);
          
          // Calculate stats from messages
          const stats = {
            totalMessages: parsedMessages.length,
            imageCount: parsedMessages.filter(m => m.attachment?.type === 'image').length,
            audioCount: parsedMessages.filter(m => m.attachment?.type === 'audio').length,
            documentCount: parsedMessages.filter(m => m.attachment?.type === 'document').length,
            videoCount: parsedMessages.filter(m => m.attachment?.type === 'video').length,
            contactCount: parsedMessages.filter(m => m.attachment?.type === 'contact').length,
          };
          setStats(stats);
          setProcessing(false);

          // Load existing AI summary if available
          if (chat.ai_summary) {
            setAiSummary(chat.ai_summary);
          }

          toast({
            title: t("toast.chatLoaded"),
            description: t("toast.foundMessages").replace("{count}", stats.totalMessages.toString()),
          });

          // Process messages with attachments that don't have AI data
          const messagesToProcess = messagesWithAi.filter(msg => 
            msg.attachment && !existingAiData[msg.id]
          );

          if (messagesToProcess.length > 0) {
            setAiProcessing(true);
            setAiStatusMessage(`Processing ${messagesToProcess.length} attachments...`);
            
            // Process each message individually
            messagesToProcess.forEach((msg, index) => {
              processMessageAttachment(storedChatId, msg, index, messagesToProcess.length);
            });
          } else {
            toast({
              title: t("toast.chatLoaded"),
              description: t("toast.allAttachmentsProcessed"),
            });
          }

          // Generate AI summary if not exists
          if (!chat.ai_summary) {
            generateAiSummary(storedChatId, chat);
          } else {
            setAiSummary(chat.ai_summary);
          }
        } else {
          // Legacy support: Download and parse ZIP file if messages_data doesn't exist
          if (!chat.storage_path) {
            throw new Error('No chat data available');
          }

          const { data: fileBlob, error: downloadError } = await supabase.storage
            .from('chat-exports')
            .download(chat.storage_path);

          if (downloadError || !fileBlob) {
            throw new Error('Failed to download file');
          }

          const file = new File([fileBlob], chat.file_name, { type: 'application/zip' });

          toast({
            title: t("toast.processingChatExportFile"),
            description: t("toast.parsingMessages"),
          });

          // Parse the file
          const parsed: ParsedChat = await parseWhatsAppZip(file, (prog, stage) => {
            setProgress(prog);
            setProgressStage(stage);
          });

          // Upload media files and continue with legacy flow
          setProgressStage(t("loading.uploadingMediaFiles"));
          
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            throw new Error('User not authenticated');
          }
          
          const messagesWithStorageUrls = await Promise.all(
            parsed.messages.map(async (msg) => {
              if (msg.attachment?.url && msg.attachment.url.startsWith('blob:')) {
                try {
                  const blobResponse = await fetch(msg.attachment.url);
                  const blob = await blobResponse.blob();
                  
                  const fileExtension = msg.attachment.fileName.split('.').pop() || 'jpg';
                  const storagePath = `${user.id}/${storedChatId}/media/${msg.id}.${fileExtension}`;
                  
                  const { error: uploadError } = await supabase.storage
                    .from('chat-exports')
                    .upload(storagePath, blob, {
                      contentType: blob.type,
                      upsert: true
                    });
                  
                  if (uploadError) {
                    console.error(`Failed to upload ${msg.attachment.fileName}:`, uploadError);
                    return msg;
                  }
                  
                  const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                    .from('chat-exports')
                    .createSignedUrl(storagePath, 31536000);
                  
                  if (signedUrlError || !signedUrlData) {
                    console.error(`Failed to get signed URL for ${msg.attachment.fileName}:`, signedUrlError);
                    return msg;
                  }
                  
                  return {
                    ...msg,
                    attachment: {
                      ...msg.attachment,
                      url: signedUrlData.signedUrl,
                      storagePath: storagePath
                    }
                  };
                } catch (error) {
                  console.error(`Error uploading ${msg.attachment.fileName}:`, error);
                  return msg;
                }
              }
              return msg;
            })
          );
          
          const existingAiData = typeof chat.attachment_ai_data === 'object' && chat.attachment_ai_data !== null 
            ? chat.attachment_ai_data as Record<string, any>
            : {};
          
          const messagesWithAi = messagesWithStorageUrls.map(msg => {
            if (msg.attachment && existingAiData[msg.id]) {
              const aiData = existingAiData[msg.id] as any;
              return {
                ...msg,
                attachment: {
                  ...msg.attachment,
                  aiDescription: aiData.aiDescription,
                  transcription: aiData.transcription
                }
              };
            }
            return msg;
          });
          
          setMessages(messagesWithAi);
          setAttachmentAiData(existingAiData as Record<string, any>);
          setStats(parsed.stats);
          setProcessing(false);

          if (chat.ai_summary) {
            setAiSummary(chat.ai_summary);
          }

          toast({
            title: t("toast.chatLoaded"),
            description: t("toast.foundMessages").replace("{count}", parsed.stats.totalMessages.toString()),
          });

          const htmlContent = generateHTMLContent(messagesWithAi);
          await supabase.from('chat_analyses').update({
            html_content: htmlContent,
            message_count: parsed.stats.totalMessages,
            image_count: parsed.stats.imageCount,
            audio_count: parsed.stats.audioCount,
            document_count: parsed.stats.documentCount,
            processing_status: 'parsed',
            messages_data: messagesWithAi as any,
          }).eq('id', storedChatId);

          const messagesToProcess = messagesWithAi.filter(msg => 
            msg.attachment && !existingAiData[msg.id]
          );

          if (messagesToProcess.length > 0) {
            setAiProcessing(true);
            setAiStatusMessage(`Processing ${messagesToProcess.length} attachments...`);
            
            messagesToProcess.forEach((msg, index) => {
              processMessageAttachment(storedChatId, msg, index, messagesToProcess.length);
            });
          } else {
            toast({
              title: t("toast.chatLoaded"),
              description: t("toast.allAttachmentsProcessed"),
            });
          }

          if (!chat.ai_summary) {
            generateAiSummary(storedChatId, chat);
          } else {
            setAiSummary(chat.ai_summary);
          }
        }
        
      } catch (error) {
        console.error('Load error:', error);
        toast({
          title: t("toast.failedToLoadChat"),
          description: error instanceof Error ? error.message : "An error occurred",
          variant: "destructive",
        });
        setProcessing(false);
      }
    };

    if (user) {
      loadChat();
    }

    // Cleanup WebSocket on unmount
    return () => {
      wsRef.current.forEach(ws => {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      });
      wsRef.current.clear();
    };
  }, [user, toast]);

  // Function to handle cost tracking
  const handleCostIncurred = async (cost: number, type: 'upload' | 'chatQueries' | 'rerunCaptions' | 'rerunSummary') => {
    if (!chatId) return;

    const newBreakdown = {
      ...costBreakdown,
      [type]: (costBreakdown[type] || 0) + cost,
    };
    const newTotal = totalCost + cost;

    setTotalCost(newTotal);
    setCostBreakdown(newBreakdown);

    // Update database
    await supabase
      .from('chat_analyses')
      .update({
        cost_tracking: {
          total: newTotal,
          breakdown: newBreakdown,
          lastUpdated: new Date().toISOString(),
        }
      })
      .eq('id', chatId);
  };

  // Function to process individual message attachment
  const processMessageAttachment = (chatId: string, message: WhatsAppMessage, index: number, total: number) => {
    const wsUrl = `wss://chlqyowphtwbmtnlxqxy.supabase.co/functions/v1/process-message?chatId=${chatId}&messageId=${message.id}`;
    const ws = new WebSocket(wsUrl);
    
    wsRef.current.set(message.id, ws);
    setProcessingMessages(prev => new Set(prev).add(message.id));

    ws.onopen = () => {
      console.log('🔌 WebSocket connected for message:', message.id);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 Message from WS:', data);

      switch (data.type) {
        case 'status':
          setAiStatusMessage(`${data.message} (${index + 1}/${total})`);
          break;

        case 'cached':
          console.log('✅ Loaded from cache:', message.id);
          // Update message with cached data
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === message.id && msg.attachment
                ? {
                    ...msg,
                    attachment: {
                      ...msg.attachment,
                      aiDescription: data.data.aiDescription,
                      transcription: data.data.transcription
                    }
                  }
                : msg
            )
          );
          setProcessingMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(message.id);
            if (newSet.size === 0) setAiProcessing(false);
            return newSet;
          });
          break;

        case 'complete':
          console.log('✅ Processed message:', message.id, data.data);
          // Update attachment_ai_data state
          setAttachmentAiData(prev => ({
            ...prev,
            [message.id]: data.data
          }));
          // Update message with new AI data
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === message.id && msg.attachment
                ? {
                    ...msg,
                    attachment: {
                      ...msg.attachment,
                      aiDescription: data.data.aiDescription,
                      transcription: data.data.transcription
                    }
                  }
                : msg
            )
          );
          setProcessingMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(message.id);
            if (newSet.size === 0) {
              setAiProcessing(false);
            }
            return newSet;
          });
          break;

        case 'error':
          console.error('❌ Error processing message:', message.id, data.message);
          setProcessingMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(message.id);
            if (newSet.size === 0) setAiProcessing(false);
            return newSet;
          });
          break;
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error for message:', message.id, error);
      setProcessingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(message.id);
        if (newSet.size === 0) setAiProcessing(false);
        return newSet;
      });
    };

    ws.onclose = () => {
      console.log('🔌 WebSocket closed for message:', message.id);
      wsRef.current.delete(message.id);
    };
  };

  // Function to initiate rerun - show cost dialog first
  const initiateRerunAI = async (messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message || !message.attachment) return;

    // Fetch current budget
    try {
      if (user) {
        const { data } = await supabase
          .from("profiles")
          .select("budget")
          .eq("id", user.id)
          .single();
        
        if (data) {
          setRerunBudget(parseFloat(String(data.budget || 0)));
        }
      }
    } catch (error) {
      console.error("Error fetching budget:", error);
    }

    // Calculate cost based on attachment type
    const cost = estimateRerunCaptionCost(message.attachment.type as 'image' | 'audio', isAdmin);
    setRerunCost(cost);
    setPendingRerunMessageId(messageId);
    setShowRerunCostDialog(true);
  };

  // Function to rerun AI processing for a specific message
  const handleRerunAI = async (messageId: string) => {
    if (!chatId) return;

    const message = messages.find(m => m.id === messageId);
    if (!message || !message.attachment) return;

    // Check and deduct budget
    try {
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("budget")
          .eq("id", user.id)
          .single();

        if (profile) {
          const currentBudget = parseFloat(String(profile.budget || 0));
          if (currentBudget < rerunCost) {
            toast({
              title: "Insufficient Budget",
              description: "Please add funds to continue.",
              variant: "destructive",
            });
            setShowRerunCostDialog(false);
            return;
          }

          // Deduct from budget
          const newBudget = currentBudget - rerunCost;
          await supabase
            .from("profiles")
            .update({ budget: newBudget })
            .eq("id", user.id);
        }
      }
    } catch (error) {
      console.error("Error handling budget:", error);
      toast({
        title: "Error",
        description: "Failed to process budget",
        variant: "destructive",
      });
      setShowRerunCostDialog(false);
      return;
    }

    try {
      // Clear cached AI data for this message
      const { data: chat, error: fetchError } = await supabase
        .from("chat_analyses")
        .select("attachment_ai_data")
        .eq("id", chatId)
        .single();

      if (fetchError || !chat) {
        toast({
          title: "Error",
          description: "Failed to load chat data",
          variant: "destructive",
        });
        return;
      }

      // Remove this message's AI data from the cache
      const attachmentAiData = (chat.attachment_ai_data as Record<string, any>) || {};
      const updatedAiData = { ...attachmentAiData };
      delete updatedAiData[messageId];

      const { error: updateError } = await supabase
        .from("chat_analyses")
        .update({ attachment_ai_data: updatedAiData })
        .eq("id", chatId);

      if (updateError) {
        toast({
          title: "Error",
          description: "Failed to clear cache",
          variant: "destructive",
        });
        return;
      }

      // Clear AI data from the message state
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === messageId && msg.attachment
            ? {
                ...msg,
                attachment: {
                  ...msg.attachment,
                  aiDescription: undefined,
                  transcription: undefined,
                }
              }
            : msg
        )
      );

      // Re-process the message
      processMessageAttachment(chatId, message, 0, 1);

      // Track cost
      handleCostIncurred(rerunCost, 'rerunCaptions');

      toast({
        title: "AI reprocessing started",
        description: "Generating new analysis...",
      });
    } catch (error) {
      console.error("Error rerunning AI:", error);
      toast({
        title: "Error",
        description: "Failed to rerun AI processing",
        variant: "destructive",
      });
    }
  };

  // Function to generate AI summary
  const generateAiSummary = async (chatId: string, chat: any) => {
    try {
      setAiStatusMessage(t("viewer.generatingSummary"));
      
      const wsUrl = `wss://chlqyowphtwbmtnlxqxy.supabase.co/functions/v1/process-chat-ws?chatId=${chatId}`;
      const ws = new WebSocket(wsUrl);

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'summary') {
          setAiSummary(data.data);
        } else if (data.type === 'complete') {
          setAiStatusMessage('');
        }
      };

      ws.onerror = (error) => {
        console.error('Error generating summary:', error);
      };
    } catch (error) {
      console.error('Failed to generate summary:', error);
    }
  };

  // Function to scroll to a specific message
  const handleScrollToMessage = (messageId: string) => {
    if (!messagesContainerRef.current) return;
    
    // Find the message element
    const messageElement = messagesContainerRef.current.querySelector(
      `[data-message-id="${messageId}"]`
    ) as HTMLElement;
    
    if (messageElement) {
      // Scroll the message into view with smooth behavior
      messageElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Add a highlight effect
      messageElement.style.transition = 'background-color 0.3s ease';
      messageElement.style.backgroundColor = 'hsl(var(--primary) / 0.1)';
      
      setTimeout(() => {
        messageElement.style.backgroundColor = '';
      }, 2000);
    }
  };

  // Function to rerun AI summary
  const handleRerunSummary = async () => {
    if (!chatId || rerunningAi) return;

    try {
      setRerunningAi(true);
      setAiSummary('');
      setAiStatusMessage(t("viewer.regeneratingSummary"));

      const { data, error } = await supabase.functions.invoke('rerun-summary', {
        body: { chatId }
      });

      if (error) {
        console.error('Failed to rerun summary:', error);
        toast({
          title: t("error.title"),
          description: error.message || t("viewer.rerunSummaryError"),
          variant: "destructive",
        });
        return;
      }

      if (data?.error) {
        toast({
          title: t("error.title"),
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      setAiSummary(data.summary);
      
      // Update cost tracking
      if (data.cost) {
        handleCostIncurred(data.cost, 'rerunSummary');
      }

      toast({
        title: t("viewer.summaryRegenerated"),
        description: t("viewer.summaryRegenerated"),
      });
    } catch (error) {
      console.error('Error rerunning summary:', error);
      toast({
        title: t("error.title"),
        description: t("viewer.rerunSummaryError"),
        variant: "destructive",
      });
    } finally {
      setRerunningAi(false);
      setAiStatusMessage('');
    }
  };

  // Subscribe to real-time AI processing updates (fallback for database updates)
  useEffect(() => {
    if (!chatId || !user) return;

    const channel = supabase
      .channel('chat-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_analyses',
          filter: `id=eq.${chatId}`,
        },
        (payload) => {
          const updated = payload.new as any;
          console.log('📡 Realtime update received:', updated);
          
          // Update AI summary
          if (updated.ai_summary && !aiSummary) {
            setAiSummary(updated.ai_summary);
          }
          
          // Update messages with new AI data from attachments
          if (updated.attachment_ai_data) {
            setMessages(prevMessages => prevMessages.map(msg => {
              if (!msg.attachment) return msg;
              
              const aiData = updated.attachment_ai_data[msg.attachment.fileName];
              if (aiData) {
                console.log(`✅ Updating message ${msg.id} with AI data:`, aiData);
                return {
                  ...msg,
                  attachment: {
                    ...msg.attachment,
                    ...aiData
                  }
                };
              }
              return msg;
            }));
          }
          
          // Update processing status
          if (updated.processing_status === 'completed') {
            setAiProcessing(false);
            setProcessing(false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatId, user, aiSummary]);

  const generateHTMLContent = (messages: WhatsAppMessage[]): string => {
    // Generate HTML representation of the chat for storage (with message IDs)
    let html = '<div class="chat-export">';
    messages.forEach(msg => {
      html += `<div class="message ${msg.isSent ? 'sent' : 'received'}" data-id="${msg.id}">`;
      html += `<div class="sender">${msg.sender}</div>`;
      html += `<div class="content">${msg.content}</div>`;
      const timestampStr = typeof msg.timestamp === 'string' ? msg.timestamp : msg.timestamp instanceof Date ? msg.timestamp.toISOString() : new Date(msg.timestamp).toISOString();
      html += `<div class="timestamp">${timestampStr}</div>`;
      if (msg.attachment) {
        const storagePathAttr = (msg.attachment as any).storagePath ? ` data-storage-path="${(msg.attachment as any).storagePath}"` : '';
        html += `<div class="attachment" data-type="${msg.attachment.type}" data-file="${msg.attachment.fileName}"${msg.attachment.url ? ` data-url="${msg.attachment.url}"` : ''}${storagePathAttr}></div>`;
      }
      html += '</div>';
    });
    html += '</div>';
    return html;
  };

  if (loading || processing) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md w-full px-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <div className="space-y-2">
            <p className="text-lg font-medium">
              {loading ? t("loading.general") : progressStage}
            </p>
            {processing && (
              <>
                <div className="w-full bg-secondary rounded-full h-2.5 overflow-hidden">
                  <div 
                    className="bg-primary h-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground">{progress}%</p>
              </>
            )}
          </div>
          {processing && (
            <p className="text-sm text-muted-foreground">
              {t("viewer.extractingMessages")}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const handleExportPDF = async (options: PdfExportOptions) => {
    try {
      setPdfProgress({ active: true, step: 'מכין נתונים...', percent: 10 });

      // Escape HTML special characters
      const escapeHtml = (text: string) => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      };
      
      // Filter messages based on options
      let messagesToExport = messages;
      
      if (options.dateRange?.from) {
        messagesToExport = messagesToExport.filter(msg => {
          const msgDate = new Date(msg.timestamp);
          const from = options.dateRange!.from!;
          const to = options.dateRange!.to || options.dateRange!.from!;
          return msgDate >= from && msgDate <= new Date(to.getTime() + 86400000);
        });
      }
      
      if (messagesToExport.length === 0) {
        throw new Error('No messages in selected date range');
      }
      
      setPdfProgress({ active: true, step: 'בונה תוכן...', percent: 30 });

      // Convert image URLs to base64 for embedding in PDF
      const imageBase64Map: Record<string, string> = {};
      if (options.includeImages) {
        const imageMessages = messagesToExport.filter(m => m.attachment?.type === 'image' && m.attachment?.url);
        const total = imageMessages.length;
        for (let i = 0; i < total; i++) {
          const msg = imageMessages[i];
          try {
            setPdfProgress({ active: true, step: `טוען תמונה ${i + 1}/${total}...`, percent: 30 + Math.round((i / total) * 20) });
            const response = await fetch(msg.attachment!.url!);
            const blob = await response.blob();
            const base64 = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(blob);
            });
            imageBase64Map[msg.id] = base64;
          } catch (e) {
            console.warn(`Failed to load image for message ${msg.id}:`, e);
          }
        }
      }

      setPdfProgress({ active: true, step: 'בונה HTML...', percent: 55 });

      const exportDate = new Date().toLocaleDateString('he-IL');
      const dateRangeText = options.dateRange?.from 
        ? options.dateRange.to
          ? `${format(options.dateRange.from, "dd/MM/yyyy")} - ${format(options.dateRange.to, "dd/MM/yyyy")}`
          : format(options.dateRange.from, "dd/MM/yyyy")
        : "כל התאריכים";
      
      // Build messages HTML
      let messagesHtml = '';
      let lastDate = '';
      messagesToExport.forEach((msg) => {
        const msgDate = new Date(msg.timestamp);
        const dateStr = msgDate.toLocaleDateString('he-IL', { 
          year: 'numeric', month: 'long', day: 'numeric' 
        });
        
        if (dateStr !== lastDate) {
          messagesHtml += `<div class="date-sep-wrap"><div class="date-separator">${dateStr}</div></div>`;
          lastDate = dateStr;
        }
        
        const formattedTime = msgDate.toLocaleTimeString('he-IL', {
          hour: '2-digit', minute: '2-digit'
        });
        
        messagesHtml += `<div class="msg-row ${msg.isSent ? 'sent' : 'received'}">`;
        messagesHtml += `<div class="bubble ${msg.isSent ? 'sent' : 'received'}">`;
        
        if (!msg.isSent) {
          messagesHtml += `<div class="sender">${escapeHtml(msg.sender)}</div>`;
        }
        
        const displayContent = msg.attachment 
          ? msg.content.replace(/<attached:[^>]+>/gi, '').trim()
          : msg.content;
        
        if (displayContent) {
          messagesHtml += `<div class="content">${escapeHtml(displayContent)}</div>`;
        }
        
        if (msg.attachment) {
          const shouldInclude = 
            (msg.attachment.type === 'image' && options.includeImages) ||
            (msg.attachment.type === 'audio' && options.includeAudioTranscriptions) ||
            (msg.attachment.type !== 'image' && msg.attachment.type !== 'audio');
          
          if (shouldInclude) {
            messagesHtml += `<div class="attachment-area">`;
            
            // Image inline
            if (msg.attachment.type === 'image' && imageBase64Map[msg.id]) {
              messagesHtml += `<div class="att-image"><img src="${imageBase64Map[msg.id]}" alt="${escapeHtml(msg.attachment.fileName || 'image')}" /></div>`;
            }
            
            // Audio indicator
            if (msg.attachment.type === 'audio') {
              messagesHtml += `<div class="att-audio">🎙️ הודעה קולית</div>`;
            }
            
            // Document indicator
            if (msg.attachment.type === 'document') {
              messagesHtml += `<div class="att-doc">📄 ${escapeHtml(msg.attachment.fileName || 'מסמך')}</div>`;
            }
            
            // Video indicator
            if (msg.attachment.type === 'video') {
              messagesHtml += `<div class="att-doc">🎬 ${escapeHtml(msg.attachment.fileName || 'וידאו')}</div>`;
            }
            
            // Contact indicator
            if (msg.attachment.type === 'contact') {
              messagesHtml += `<div class="att-doc">👤 ${escapeHtml(msg.attachment.contactData?.name || msg.attachment.fileName || 'איש קשר')}</div>`;
            }
            
            // AI Description
            if (msg.attachment.aiDescription && options.includeAiDescriptions) {
              messagesHtml += `<div class="ai-box"><span class="ai-icon">✨</span><div><span class="ai-label">ניתוח AI</span> ${escapeHtml(msg.attachment.aiDescription)}</div></div>`;
            }
            
            // Audio transcription
            if (msg.attachment.transcription && options.includeAudioTranscriptions) {
              messagesHtml += `<div class="trans-box"><span class="trans-icon">✨</span><div><span class="trans-label">תמלול</span> ${escapeHtml(msg.attachment.transcription)}</div></div>`;
            }
            
            messagesHtml += `</div>`;
          }
        }
        
        messagesHtml += `<div class="timestamp">${formattedTime}</div>`;
        messagesHtml += `</div></div>`; // close bubble + msg-row
      });

      setPdfProgress({ active: true, step: 'מייצר PDF...', percent: 70 });

      const fullHtml = `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="UTF-8">
  <title>יצוא שיחה - ${escapeHtml(chatFileName || 'WhatsApp Chat')}</title>
  <style>
    @page {
      size: A4;
      margin: 12mm 12mm 18mm 12mm;
    }
    
    * { box-sizing: border-box; margin: 0; padding: 0; }
    
    body {
      font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
      font-size: 12px;
      line-height: 1.5;
      color: #1a1a1a;
      direction: rtl;
      background: #e5ddd5;
      padding: 10px;
    }
    
    .pdf-header {
      background: linear-gradient(135deg, #128c7e, #075e54);
      color: white;
      padding: 16px 20px;
      margin-bottom: 12px;
      border-radius: 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    
    .pdf-header h1 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
    
    .pdf-header .info {
      font-size: 10px;
      opacity: 0.9;
      text-align: left;
    }
    
    .chat-area {
      background: #e5ddd5;
      padding: 8px 0;
    }
    
    .msg-row {
      display: flex;
      margin-bottom: 4px;
      padding: 0 8px;
    }
    
    .msg-row.sent { justify-content: flex-start; }
    .msg-row.received { justify-content: flex-end; }
    
    .bubble {
      max-width: 75%;
      padding: 6px 10px 4px 10px;
      border-radius: 8px;
      position: relative;
      box-shadow: 0 1px 1px rgba(0,0,0,0.13);
      page-break-inside: avoid;
    }
    
    .bubble.sent {
      background: #dcf8c6;
    }
    
    .bubble.received {
      background: #ffffff;
    }
    
    .sender {
      font-weight: 600;
      font-size: 11px;
      margin-bottom: 2px;
      color: #6a1b9a;
    }
    
    .content {
      word-wrap: break-word;
      white-space: pre-wrap;
      color: #303030;
      font-size: 12px;
      line-height: 1.4;
    }
    
    .timestamp {
      font-size: 9px;
      color: #999;
      text-align: left;
      margin-top: 2px;
    }
    
    .attachment-area {
      margin-top: 6px;
    }
    
    .att-image {
      margin: 4px 0;
    }
    
    .att-image img {
      max-width: 100%;
      max-height: 280px;
      border-radius: 6px;
      object-fit: contain;
      display: block;
    }
    
    .att-audio {
      padding: 8px 12px;
      background: rgba(0,0,0,0.05);
      border-radius: 20px;
      font-size: 11px;
      color: #555;
      display: inline-block;
    }
    
    .att-doc {
      padding: 8px 12px;
      background: rgba(0,0,0,0.05);
      border-radius: 6px;
      font-size: 11px;
      color: #333;
    }
    
    .ai-box {
      margin-top: 6px;
      padding: 6px 8px;
      background: rgba(0,0,0,0.04);
      border-radius: 6px;
      border: 1px solid rgba(0,0,0,0.06);
      font-size: 10px;
      color: #555;
      display: flex;
      gap: 6px;
      align-items: flex-start;
    }
    
    .ai-icon, .trans-icon {
      flex-shrink: 0;
      font-size: 12px;
    }
    
    .ai-label {
      font-weight: 600;
      color: #128c7e;
    }
    
    .trans-box {
      margin-top: 6px;
      padding: 6px 8px;
      background: rgba(0,0,0,0.04);
      border-radius: 6px;
      border: 1px solid rgba(0,0,0,0.06);
      font-size: 10px;
      color: #555;
      display: flex;
      gap: 6px;
      align-items: flex-start;
    }
    
    .trans-label {
      font-weight: 600;
      color: #e65100;
    }
    
    .date-separator {
      text-align: center;
      margin: 12px auto;
      padding: 4px 14px;
      background: #e1f2fb;
      border-radius: 8px;
      font-size: 10px;
      color: #555;
      display: inline-block;
      width: auto;
      box-shadow: 0 1px 1px rgba(0,0,0,0.08);
    }
    
    .date-sep-wrap {
      text-align: center;
      margin: 8px 0;
    }
    
    .pdf-footer {
      margin-top: 16px;
      padding-top: 8px;
      border-top: 1px solid #ccc;
      display: flex;
      justify-content: space-between;
      font-size: 9px;
      color: #999;
    }
    
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .pdf-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .bubble { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="pdf-header">
    <div>
      <h1>יצוא שיחה</h1>
      <div>${escapeHtml(chatFileName || 'WhatsApp Chat')}</div>
    </div>
    <div class="info">
      <div>תאריך יצוא: ${exportDate}</div>
      <div>טווח: ${dateRangeText}</div>
      <div>${messagesToExport.length} הודעות</div>
    </div>
  </div>
  
  <div class="chat-area">
  ${messagesHtml}
  </div>
  
  <div class="pdf-footer">
    <div>${escapeHtml(chatFileName || 'WhatsApp Chat')}</div>
    <div>יוצא באמצעות WhatAI</div>
  </div>
</body>
</html>`;

      // Use a hidden iframe + browser print-to-PDF
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.top = '-10000px';
      printFrame.style.left = '-10000px';
      printFrame.style.width = '800px';
      printFrame.style.height = '600px';
      printFrame.style.border = 'none';
      document.body.appendChild(printFrame);

      await new Promise<void>((resolve, reject) => {
        try {
          const frameDoc = printFrame.contentDocument || printFrame.contentWindow?.document;
          if (!frameDoc) {
            reject(new Error('Cannot access iframe document'));
            return;
          }

          frameDoc.open();
          frameDoc.write(fullHtml);
          frameDoc.close();

          // Wait for content to render, then trigger print
          setTimeout(() => {
            try {
              setPdfProgress({ active: true, step: 'פותח חלון הדפסה...', percent: 90 });
              
              const frameWindow = printFrame.contentWindow;
              if (!frameWindow) {
                reject(new Error('Cannot access iframe window'));
                return;
              }

              frameWindow.focus();
              frameWindow.print();
              
              // Cleanup after a short delay
              setTimeout(() => {
                if (document.body.contains(printFrame)) {
                  document.body.removeChild(printFrame);
                }
                resolve();
              }, 1000);
            } catch (e) {
              reject(e);
            }
          }, 500);
        } catch (e) {
          reject(e);
        }
      });
      
      toast({
        title: "PDF מוכן",
        description: `${messagesToExport.length} הודעות יוצאו בהצלחה. שמור כ-PDF בחלון ההדפסה.`,
      });
      
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast({
        title: "שגיאה ביצוא",
        description: error instanceof Error ? error.message : "היצוא נכשל",
        variant: "destructive",
      });
    } finally {
      setPdfProgress({ active: false, step: '', percent: 0 });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* PDF Export Progress Overlay */}
      {pdfProgress.active && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-card rounded-xl p-6 shadow-lg max-w-sm w-full mx-4 space-y-4">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <h3 className="font-semibold text-lg">מייצר PDF...</h3>
            </div>
            <div className="space-y-2">
              <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-primary h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${pdfProgress.percent}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground text-center">{pdfProgress.step}</p>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="bg-card border-b shadow-soft sticky top-0 z-10">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl sm:text-2xl font-bold text-primary">
                {t("viewer.chatAnalysis")}
              </h1>
              {chatFileName && (
                <p className="text-sm text-muted-foreground truncate">
                  {chatFileName}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {chatId && (
                <ReprocessChatButton chatId={chatId} />
              )}
              <Button onClick={() => setShowShareDialog(true)} variant="outline" size="sm">
                <Share2 className="mr-2 h-4 w-4" />
                {t("share.shareChat")}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Share Dialog */}
      {chatId && (
        <ShareDialog 
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          chatId={chatId}
        />
      )}

      {/* Filter Bar */}
      <FilterBar 
        activeFilter={activeFilter} 
        onFilterChange={setActiveFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        onExportPDF={() => handleExportPDF({ includeImages: true, includeAudioTranscriptions: true, includeAiDescriptions: true })}
        onOpenExportDialog={() => setShowPdfExportDialog(true)}
      />

      {/* PDF Export Dialog */}
      <PdfExportDialog
        open={showPdfExportDialog}
        onOpenChange={setShowPdfExportDialog}
        onExport={handleExportPDF}
        currentDateRange={dateRange}
        messages={messages}
        chatFileName={chatFileName}
      />

      {/* AI Chat Panel - Fixed position floating overlay */}
      <div className="fixed bottom-24 left-4 z-30">
        <AiChatPanel 
          chatContext={JSON.stringify({
            fileName: chatFileName,
            messageCount: stats.totalMessages,
            messages: messages.map(m => ({
              sender: m.sender,
              content: m.content,
              timestamp: typeof m.timestamp === 'string' ? m.timestamp : m.timestamp instanceof Date ? m.timestamp.toISOString() : new Date(m.timestamp).toISOString(),
              attachment: m.attachment ? {
                type: m.attachment.type,
                aiDescription: m.attachment.aiDescription,
                transcription: m.attachment.transcription
              } : undefined
            })),
            aiSummary
          })}
          language={chatLanguage}
          onCostIncurred={(cost) => handleCostIncurred(cost, 'chatQueries')}
        />
      </div>

      {/* Cost Confirmation Dialogs */}
      <CostConfirmDialog
        open={showRerunCostDialog}
        onOpenChange={setShowRerunCostDialog}
        onConfirm={() => {
          setShowRerunCostDialog(false);
          if (pendingRerunMessageId) {
            handleRerunAI(pendingRerunMessageId);
            setPendingRerunMessageId(null);
          }
        }}
        cost={rerunCost}
        title={t("viewer.confirmRerunTitle")}
        description={t("viewer.confirmRerunDesc")}
        currentBudget={rerunBudget}
      />

      {/* Main Content */}
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Chat Messages */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-xl sm:rounded-2xl shadow-soft p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl font-bold">{t("viewer.messages")}</h2>
                {(activeFilter || dateRange?.from || searchQuery) && (
                  <p className="text-sm text-muted-foreground">
                    {t("viewer.showingMessages")
                      .replace("{filtered}", filteredMessages.length.toString())
                      .replace("{total}", messages.length.toString())}
                  </p>
                )}
              </div>
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">{t("viewer.noMessages")}</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate("/upload")}
                  >
                    {t("viewer.uploadChat")}
                  </Button>
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">{t("viewer.noMatchingMessages")}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => {
                      setActiveFilter(null);
                      setDateRange(undefined);
                      setSearchQuery("");
                    }}
                  >
                    {t("viewer.clearFilters")}
                  </Button>
                </div>
              ) : (
                <div 
                  ref={messagesContainerRef}
                  className="space-y-3 sm:space-y-4 max-h-[500px] sm:max-h-[600px] overflow-y-auto"
                >
                  {filteredMessages.map((message) => (
                    <div key={message.id} data-message-id={message.id}>
                      <ChatMessage 
                        message={message}
                        isProcessing={processingMessages.has(message.id)}
                        onRerunAI={initiateRerunAI}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6">
              <div className="bg-card rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-chat">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary mb-1 sm:mb-2" />
                <p className="text-xl sm:text-2xl font-bold">{stats.totalMessages}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.messagesLabel")}</p>
              </div>
              <div className="bg-card rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-chat">
                <Image className="w-5 h-5 sm:w-6 sm:h-6 text-accent mb-1 sm:mb-2" />
                <p className="text-xl sm:text-2xl font-bold">{stats.imageCount}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.imagesLabel")}</p>
              </div>
              <div className="bg-card rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-chat">
                <Mic className="w-5 h-5 sm:w-6 sm:h-6 text-primary mb-1 sm:mb-2" />
                <p className="text-xl sm:text-2xl font-bold">{stats.audioCount}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.audioLabel")}</p>
              </div>
              <div className="bg-card rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-chat">
                <FileIcon className="w-5 h-5 sm:w-6 sm:h-6 text-accent mb-1 sm:mb-2" />
                <p className="text-xl sm:text-2xl font-bold">{stats.documentCount}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{t("viewer.documentsLabel")}</p>
              </div>
            </div>
          </div>

          {/* Sidebar - Timeline & Summary */}
          <div className="space-y-4 sm:space-y-6">
            <Timeline 
              messages={messages} 
              onDateClick={handleScrollToMessage}
            />
            
            {/* Cost Tracker */}
            <CostTracker 
              totalCost={totalCost} 
              breakdown={costBreakdown}
            />
            
            <div className="bg-card rounded-xl sm:rounded-2xl shadow-soft p-4 sm:p-6">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <h3 className="text-base sm:text-lg font-bold">{t("viewer.aiSummaryTitle")}</h3>
                {aiSummary && !aiProcessing && !rerunningAi && (
                  <Button
                    onClick={handleRerunSummary}
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    {t("viewer.rerunSummary")}
                  </Button>
                )}
              </div>
              {aiProcessing || rerunningAi ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {aiStatusMessage || t("viewer.analyzingConversation")}
                    </p>
                  </div>
                  {aiProgress > 0 && (
                    <div className="space-y-1">
                      <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-primary h-full transition-all duration-300 ease-out"
                          style={{ width: `${aiProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground text-right">{aiProgress}%</p>
                    </div>
                  )}
                </div>
              ) : aiSummary ? (
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {aiSummary}
                </p>
              ) : (
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t("viewer.noSummaryYet")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Viewer;
